// src/contexts/AuthContext.js - OAuth 2.0 Version
import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  checkTwitterLogin,
  getTwitterProfile,
  initiateTwitterLogin,
  logoutTwitter,
  handleOAuthReturn
} from '../lib/twitterAuth';

const AuthContext = createContext();

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkAuthStatus = async () => {
    console.group('🔄 checkAuthStatus');
    try {
      setError(null);
      console.log('1. Checking OAuth authentication...');

      const isLoggedIn = await checkTwitterLogin();

      if (isLoggedIn) {
        console.log('2. User is authenticated, fetching profile...');
        const profile = await getTwitterProfile();
        console.log('3. Profile fetched automatically:', profile);

        setUser(profile);
        setIsAuthenticated(true);
        console.log('4. Authentication state updated');
      } else {
        console.log('2. User is not authenticated');
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('❌ Auth check failed:', error);
      setError(error.message);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
      console.log('5. Loading complete');
      console.groupEnd();
    }
  };

  useEffect(() => {
    console.log('🎯 AuthProvider mounted');

    // Check if this is an OAuth callback
    const oauthResult = handleOAuthReturn();

    if (oauthResult) {
      if (oauthResult.success) {
        console.log('✅ OAuth callback successful');
        // Check auth status to get user data
        checkAuthStatus();
      } else {
        console.log('❌ OAuth callback failed:', oauthResult.error);
        setError(oauthResult.message || 'Authentication failed');
        setLoading(false);
      }
    } else {
      // Normal page load - check existing auth status
      checkAuthStatus();
    }
  }, []);

  // Login function - redirects to Twitter OAuth
  const login = async () => {
    console.log('🔗 Starting Twitter OAuth login...');

    try {
      setError(null);

      // This redirects to Twitter - user will be redirected back automatically
      // with their username and profile data
      initiateTwitterLogin();

    } catch (error) {
      console.error('❌ Login failed:', error);
      setError('Failed to start authentication');
    }
  };

  const logout = async () => {
    console.log('🚪 Logging out...');

    try {
      setError(null);

      const success = await logoutTwitter();

      if (success) {
        setUser(null);
        setIsAuthenticated(false);
        console.log('✅ Logout complete');
      } else {
        setError('Failed to logout');
      }

    } catch (error) {
      console.error('❌ Logout failed:', error);
      setError('Failed to logout');
    }
  };

  const refreshProfile = async () => {
    if (!isAuthenticated) return;

    try {
      setError(null);
      const profile = await getTwitterProfile();
      setUser(profile);
      console.log('✅ Profile refreshed');
    } catch (error) {
      console.error('❌ Failed to refresh profile:', error);
      setError('Failed to refresh profile');
    }
  };

  const clearError = () => {
    setError(null);
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    error,
    login,
    logout,
    checkAuthStatus,
    refreshProfile,
    clearError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}